import { z } from 'zod';
import { BiaImpactAssessmentInputSchema } from './bia-impact-configuration.schema.js';
import { optionalFinanceCurrencySchema } from './finance-currency.schema.js';

export const BiaImpactStatusSchema = z.enum(['very_low', 'low', 'medium', 'high', 'critical']);

export const ImpactAssessmentSchema = z.object({
    scores: z.array(z.object({
        categoryKey: z.string(),
        values: z.record(z.string()),
    })).optional(),
    horizonSummaries: z.array(z.object({
        horizonId: z.string(),
        aggregateScore: z.number().optional().nullable(),
        severityKey: z.string().optional(),
        label: z.string().optional(),
    })).optional(),
    recovery: z.object({
        calculationModel: z.string().optional(),
        mtpdHorizonId: z.string().nullable().optional(),
        mtpdDisplay: z.string().nullable().optional(),
        rtoHorizonId: z.string().nullable().optional(),
        rtoDisplay: z.string().nullable().optional(),
        rpoHorizonId: z.string().nullable().optional(),
        rpoDisplay: z.string().nullable().optional(),
        rtoCappedByMtpd: z.boolean().optional(),
        rpoCappedByRto: z.boolean().optional(),
        impactStatus: BiaImpactStatusSchema.optional().nullable(),
        impactStatusAverage: z.number().optional().nullable(),
        warnings: z.array(z.string()).optional(),
        computedAt: z.union([z.date(), z.string()]).optional(),
    }).optional(),
}).optional();

export const RpoCalculationSchema = z.object({
    id: z.string().optional(),
    _id: z.string().optional(),
    application: z.string().optional(),
    transaction: z.string().optional(),
    type: z.string().optional(),
    avgTransHr: z.string().optional(),
    backupFreq: z.string().optional(),
    restoreTime: z.string().optional(),
    transLoss: z.string().optional(),
    maxAllowableLoss: z.string().optional(),
    rpo: z.string().optional(),
}).passthrough();

export const RpoRecommendationSchema = z.object({
    id: z.string().optional(),
    _id: z.string().optional(),
    application: z.string().optional(),
    backupFreq: z.string().optional(),
    restoreTime: z.string().optional(),
    recommendedRpo: z.string().optional(),
    remarks: z.string().optional()
}).passthrough();

export const BiaRecoveryResourceSchema = z.object({
    name: z.string().min(1, "form.validation.nameRequired"),
    type: z.string().optional(),
    details: z.string().optional(),
    normalLevel: z.string().optional(),
    t2h: z.string().optional(),
    t8h: z.string().optional(),
    t24h: z.string().optional(),
    t2d: z.string().optional(),
    t1w: z.string().optional(),
    other: z.string().optional(),
    availability: z.string().optional(),
    criticality: z.string().optional(),
});

export const BiaSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.string().optional(),
    tenantId: z.string().optional(),
    biaId: z.string().trim().min(1, "form.validation.required"),
    name: z.string().min(1, "form.validation.nameRequired"),
    description: z.string().optional(),
    minimumServiceLevel: z.string().optional(),
    continuityRecoveryNotes: z.string().optional(),
    category: z.string().optional(),

    process: z.string().optional().nullable(),
    unit: z.string().optional().nullable(),
    location: z.string().optional().nullable(),

    rto: z.string().optional(),
    rpo: z.string().optional(),
    mtpd: z.string().optional(),
    downtimeCost: z.string().optional(),
    currency: optionalFinanceCurrencySchema,

    criticality: z.string().optional(),
    status: BiaImpactStatusSchema.optional(),
    impactStatusAverage: z.number().optional().nullable(),
    state: z.string().optional(),

    lastReviewDate: z.string().optional(),
    nextReview: z.string().optional(),

    partyInstanceId: z.string().optional().nullable(),

    impactAssessment: ImpactAssessmentSchema,

    processes: z.array(z.string()).optional(),
    vendors: z.array(z.string()).optional(),
    assets: z.array(z.string()).optional(),
    employees: z.array(z.string()).optional(),

    recoveryResources: z.array(z.string()).optional(),
    relatedBcps: z.array(z.string()).optional(),

    documents: z.array(z.string()).optional(),
    customFields: z.record(z.unknown()).optional(),

    createdAt: z.union([z.date(), z.string()]).optional(),
    updatedAt: z.union([z.date(), z.string()]).optional(),
});

export const CreateBiaSchema = BiaSchema.omit({ id: true, _id: true, createdAt: true, updatedAt: true });
export const UpdateBiaSchema = BiaSchema.partial();

// Frontend Form Schema (no .refine() — RTO vs MTPD check stays in BiaForm.jsx)
export const BiaFormSchema = z.object({
    biaId: z.string().trim().min(1, "form.validation.required"),
    name: z.string().min(1, "form.validation.nameRequired"),
    description: z.string().optional(),
    minimumServiceLevel: z.string().optional(),
    continuityRecoveryNotes: z.string().optional(),
    category: z.string().optional(),
    location: z.string().optional(),
    unit: z.string().optional(),
    downtimeCost: z.string().optional(),
    currency: optionalFinanceCurrencySchema,
    criticality: z.string().optional(),
    state: z.string().optional(),
    lastReviewDate: z.string().optional(),
    nextReview: z.string().optional(),
});
